import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import type { IProductTypeMapping } from '@/api/constants-mapping/types';
import { DeleteConfirmDialog } from '@/app/(client)/company-settings/-components/constantsMapping/DeleteConfirmDialog';
import {
  productTypeMappingKeys,
  useCreateProductTypeMappings,
  useDeleteProductTypeMappings,
  usePatchProductTypeMapping,
  useProductTypeMappings,
  useProductTypesDropdown,
} from '@/app/(client)/company-settings/-components/constantsMapping/hooks/useProductTypeMappings';
import { MappingTable } from '@/app/(client)/company-settings/-components/constantsMapping/MappingTable';
import { ProductTypeCreateDialog } from '@/app/(client)/company-settings/-components/constantsMapping/ProductTypeCreateDialog';
import type { ColumnDef } from '@/components/shared/DataTable/tanstack';
import type { FilterDef } from '@/components/shared/DataTable/types';
import { TextValue, UpdatedAtValue } from '@/components/shared/ValueRenderers';
import { EmptyState } from '@/components/ui/empty-state';
import { usePermissions } from '@/hooks/usePermissions';
import { useTable } from '@/hooks/useTable';
import { useTranslator } from '@/hooks/useTranslator';

export const Route = createFileRoute(
  '/(client)/company-settings/(pages)/constants-mapping/product-types/',
)({
  component: ProductTypesPage,
});

function ProductTypesPage() {
  const { getTranslation: t } = useTranslator({ page: 'constantsMapping' });
  const queryClient = useQueryClient();
  const table = useTable('constants-mapping-product-types');
  const perms = usePermissions()['constant-mapping'];

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isFetching, isError } = useProductTypeMappings({
    page: table.page,
    limit: table.limit,
    ...(table.queryParams.sort !== undefined
      ? { sort: table.queryParams.sort }
      : {}),
    ...(table.queryParams.filter !== undefined
      ? { filter: table.queryParams.filter }
      : {}),
  });
  const { data: dropdownData } = useProductTypesDropdown();
  const patchMutation = usePatchProductTypeMapping();
  const deleteMutation = useDeleteProductTypeMappings();
  const createMutation = useCreateProductTypeMappings();

  if (isError)
    return (
      <EmptyState
        title={t('error.title')}
        description={t('error.description')}
      />
    );

  const productTypesDropdown = dropdownData?.data ?? [];

  const columns: ColumnDef<IProductTypeMapping>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      header: t('productType.columns.productType'),
      enableSorting: true,
      cell: ({ getValue }) => (
        <TextValue
          value={getValue<string | null>()}
          emptyNode={<span className='text-xs text-muted-foreground'>—</span>}
        />
      ),
    },
    {
      id: 'retracedProductTypeId',
      accessorKey: 'retracedProductTypeId',
      header: t('columns.retracedId'),
      enableSorting: true,
      cell: ({ getValue }) => (
        <TextValue
          value={getValue<string | null>()}
          emptyNode={<span className='text-xs text-muted-foreground'>—</span>}
        />
      ),
    },
    {
      id: 'parentName',
      accessorKey: 'parentName',
      header: t('productType.columns.productLevel'),
      enableSorting: true,
      cell: ({ getValue }) => (
        <TextValue
          value={getValue<string | null>()}
          emptyNode={<span className='text-xs text-muted-foreground'>—</span>}
        />
      ),
    },
    {
      id: 'externalName',
      accessorKey: 'externalName',
      header: t('productType.columns.yourName'),
      enableSorting: true,
      meta: {
        editor: 'text',
        editKey: 'externalName',
      },
      cell: ({ getValue }) => <TextValue value={getValue<string | null>()} />,
    },
    {
      id: 'externalId',
      accessorKey: 'externalId',
      header: t('productType.columns.constantId'),
      enableSorting: true,
      meta: {
        editor: 'text',
        editKey: 'externalId',
      },
      cell: ({ getValue }) => <TextValue value={getValue<string | null>()} />,
    },
    {
      id: 'updatedAt',
      accessorKey: 'updatedAt',
      header: t('columns.updatedAt'),
      enableSorting: true,
      cell: ({ row }) => (
        <UpdatedAtValue
          value={row.original.updatedAt}
          userName={row.original.updatedByUserName ?? null}
          companyName={row.original.updatedByCompanyName ?? null}
        />
      ),
    },
  ];

  const filterDefs: FilterDef<IProductTypeMapping>[] = [
    { id: 'name', label: t('productType.columns.productType'), type: 'text' },
    {
      id: 'parentName',
      label: t('productType.columns.productLevel'),
      type: 'text',
    },
    {
      id: 'externalName',
      label: t('productType.columns.yourName'),
      type: 'text',
    },
    {
      id: 'externalId',
      label: t('productType.columns.constantId'),
      type: 'text',
    },
  ];

  const handleCellCommit = async (
    _rowIndex: number,
    colId: string,
    rawValue: unknown,
    row: IProductTypeMapping,
  ) => {
    try {
      await patchMutation.mutateAsync({
        id: row.id,
        body: {
          externalName:
            colId === 'externalName'
              ? String(rawValue ?? '')
              : (row.externalName ?? ''),
          externalId:
            colId === 'externalId'
              ? String(rawValue ?? '')
              : (row.externalId ?? ''),
        },
      });
      void queryClient.invalidateQueries({
        queryKey: productTypeMappingKeys.all(),
      });
    } catch {
      toast.error(t('toast.saveError'));
      throw new Error('patch failed');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync([...selectedIds]);
      void queryClient.invalidateQueries({
        queryKey: productTypeMappingKeys.all(),
      });
      toast.success(
        t('toast.deleteSuccess', { count: String(selectedIds.size) }),
      );
      setSelectedIds(new Set());
      setDeleteOpen(false);
    } catch {
      toast.error(t('toast.deleteError'));
    }
  };

  const handleCreate = async (
    rows: {
      retracedProductTypeId: string;
      externalId: string;
      externalName: string;
    }[],
  ) => {
    try {
      await createMutation.mutateAsync(rows);
      void queryClient.invalidateQueries({
        queryKey: productTypeMappingKeys.all(),
      });
      toast.success(t('toast.createSuccess'));
      setCreateOpen(false);
    } catch {
      toast.error(t('toast.createError'));
    }
  };

  return (
    <>
      <MappingTable
        columns={columns}
        filterDefs={filterDefs}
        data={data?.data ?? []}
        isFetching={isFetching}
        totalCount={data?.metadata.pagination.total ?? 0}
        table={table}
        storageKey='constants-mapping-product-types'
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        rowIdKey='id'
        onAdd={() => setCreateOpen(true)}
        onDelete={() => setDeleteOpen(true)}
        isDeleting={deleteMutation.isPending}
        emptyTitle={t('productType.empty.title')}
        emptyDescription={t('productType.empty.description')}
        onCellCommit={handleCellCommit}
        canCreate={perms.canCreate}
        canEdit={perms.canEdit}
        canDelete={perms.canDelete}
      />
      <DeleteConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onConfirm={handleDelete}
        count={selectedIds.size}
      />
      <ProductTypeCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        productTypesDropdown={productTypesDropdown}
        onConfirm={handleCreate}
        isLoading={createMutation.isPending}
      />
    </>
  );
}
